import { useState } from 'react'
import { useAuth } from '@talktoast/shared'
import { validateEmail, sanitizeEmail, sanitizeText, validatePasswordStrength } from '@talktoast/shared'
import { Button, Input } from '@talktoast/ui'

interface SignUpFormProps {
  onSwitchToLogin: () => void
  onGuestContinue: () => void
}

export default function SignUpForm({ onSwitchToLogin, onGuestContinue }: SignUpFormProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [formError, setFormError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const { signUpWithEmail, signInWithGoogle, signInWithApple } = useAuth()

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault()

    // Sanitize inputs
    const sanitizedEmail = sanitizeEmail(email)
    const sanitizedDisplayName = sanitizeText(displayName, 100)

    // Clear all errors at the start of each submission attempt
    setFormError('')
    setPasswordError('')
    setConfirmPasswordError('')

    // Validate all fields present
    if (!sanitizedEmail || !password || !sanitizedDisplayName) {
      setFormError('All fields are required')
      return
    }

    // Validate email format
    if (!validateEmail(sanitizedEmail)) {
      setFormError('Please enter a valid email address')
      return
    }

    // Validate display name length
    if (sanitizedDisplayName.length < 2) {
      setFormError('Display name must be at least 2 characters')
      return
    }

    // Check passwords match
    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match')
      return
    }

    // Validate password strength
    const passwordValidation = validatePasswordStrength(password)
    if (!passwordValidation.valid) {
      setPasswordError(passwordValidation.errors[0]) // Show first error
      return
    }

    setIsLoading(true)

    try {
      await signUpWithEmail(sanitizedEmail, password, sanitizedDisplayName)
    } catch (err: any) {
      setFormError(err.message || 'Failed to create account')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSocialLogin = async (provider: 'google' | 'apple') => {
    setIsLoading(true)
    setFormError('')

    try {
      switch (provider) {
        case 'google':
          await signInWithGoogle()
          break
        case 'apple':
          await signInWithApple()
          break
      }
    } catch (err: any) {
      setFormError(err.message || `Failed to sign up with ${provider}`)
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Create account</h2>
        <p className="text-gray-600 mt-2">Join TalkToast and start planning meals</p>
      </div>

      {formError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">{formError}</p>
        </div>
      )}

      {/* Social Login Buttons */}
      <div className="space-y-3">
        <Button
          variant="outline"
          fullWidth
          size="lg"
          onClick={() => handleSocialLogin('google')}
          disabled={isLoading}
          className="justify-center gap-3 bg-white"
        >
          {/* Google logo SVG - Official brand colors (DO NOT CHANGE)
              These hex values are required by Google's brand guidelines:
              #4285F4 (Blue), #34A853 (Green), #FBBC05 (Yellow), #EA4335 (Red) */}
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign up with Google
        </Button>

        <Button
          variant="primary"
          fullWidth
          size="lg"
          onClick={() => handleSocialLogin('apple')}
          disabled={isLoading}
          className="justify-center gap-3 bg-black hover:bg-gray-800"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          Sign up with Apple
        </Button>

      </div>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">Or sign up with email</span>
        </div>
      </div>

      {/* Email Sign Up Form */}
      <form onSubmit={handleEmailSignUp} className="space-y-4">
        <Input
          id="displayName"
          label="Full Name"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Your full name"
          required
        />

        <Input
          id="email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.email@example.com"
          required
        />

        <Input
          id="password"
          label="Password"
          type="password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setPasswordError('') }}
          placeholder="12+ characters, mixed case, numbers, symbols"
          helperText="Must include uppercase, lowercase, numbers, and special characters"
          error={passwordError}
          required
          minLength={12}
        />

        <Input
          id="confirmPassword"
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => { setConfirmPassword(e.target.value); setConfirmPasswordError('') }}
          placeholder="Confirm your password"
          error={confirmPasswordError}
          required
        />

        <Button
          type="submit"
          variant="primary"
          fullWidth
          size="lg"
          disabled={isLoading}
        >
          {isLoading ? 'Creating account...' : 'Create Account'}
        </Button>
      </form>

      <div className="space-y-4 pt-4 border-t border-gray-200">
        <Button
          variant="secondary"
          fullWidth
          size="lg"
          onClick={onGuestContinue}
          disabled={isLoading}
        >
          Continue as Guest
        </Button>

        <p className="text-center text-sm text-gray-600">
          Already have an account?{' '}
          <button
            onClick={onSwitchToLogin}
            className="text-toast-gold-700 font-medium hover:text-toast-gold-800"
          >
            Sign in
          </button>
        </p>
      </div>

      <div className="text-xs text-gray-500 text-center">
        By signing up, you agree to our Terms of Service and Privacy Policy
      </div>
    </div>
  )
}